import { useCallback, useEffect, useRef, useState } from "react";

import { useRunPromptMutation } from "@/pages/Private/redux/column/column.api";
import { ColumnValueInfo } from "@/pages/Private/pages/List/schema/columnValue";

export const TEST_STEPS = ["composing", "resolvingVariables", "aiWriting", "assembling"] as const;

export type TestStep = (typeof TEST_STEPS)[number];

export type TestStepState = "pending" | "active" | "done";

const STEP_DELAYS_MS = [650, 800, 1400, 650];

type RunInput = {
	listPersonId: number;
	message: string;
	premiumMessage?: string;
};

export type TestMessageEntry = {
	original: string;
	final: string;
	columnValues: ColumnValueInfo[];
};

type TestResult = {
	messages: TestMessageEntry[];
};

/**
 * Wraps the existing `runPrompt` mutation with a 4-step client-side animated
 * timeline matching the mockup. Step transitions advance on timers; when the
 * mutation resolves, any remaining "pending" steps complete instantly so the
 * UI never lies about progress that's already finished.
 */
export const useTestMessage = () => {
	const [runPrompt] = useRunPromptMutation();

	const [stepStates, setStepStates] = useState<Record<TestStep, TestStepState>>({
		composing: "pending",
		resolvingVariables: "pending",
		aiWriting: "pending",
		assembling: "pending",
	});

	const [result, setResult] = useState<TestResult | null>(null);
	const [isRunning, setIsRunning] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const timersRef = useRef<number[]>([]);
	const requestIdRef = useRef<number>(0);

	const clearTimers = useCallback(() => {
		timersRef.current.forEach((id) => window.clearTimeout(id));
		timersRef.current = [];
	}, []);

	useEffect(() => {
		return () => clearTimers();
	}, [clearTimers]);

	const resetSteps = useCallback(() => {
		setStepStates({
			composing: "pending",
			resolvingVariables: "pending",
			aiWriting: "pending",
			assembling: "pending",
		});
	}, []);

	const completeAllSteps = useCallback(() => {
		setStepStates({
			composing: "done",
			resolvingVariables: "done",
			aiWriting: "done",
			assembling: "done",
		});
	}, []);

	const run = useCallback(
		async ({ listPersonId, message, premiumMessage }: RunInput) => {
			requestIdRef.current += 1;
			const myRequestId = requestIdRef.current;

			clearTimers();
			resetSteps();
			setResult(null);
			setError(null);
			setIsRunning(true);

			// Activate step 0 immediately.
			setStepStates((s) => ({ ...s, composing: "active" }));

			let cumulative = 0;

			TEST_STEPS.forEach((step, index) => {
				const stepDuration = STEP_DELAYS_MS[index];

				// Mark current step done when its duration elapses, and the next step
				// active (if any). The last step stays "active" until the mutation
				// settles.
				const activateNextAt = cumulative + stepDuration;

				const timer = window.setTimeout(() => {
					if (requestIdRef.current !== myRequestId) {
						return;
					}

					setStepStates((s) => {
						const next = { ...s, [step]: "done" as TestStepState };
						const nextStep = TEST_STEPS[index + 1];

						if (nextStep && next[nextStep] === "pending") {
							next[nextStep] = "active";
						}

						return next;
					});
				}, activateNextAt);

				timersRef.current.push(timer);
				cumulative = activateNextAt;
			});

			try {
				const inputs = [message, premiumMessage].filter((m): m is string => Boolean(m));
				const responses = await Promise.all(
					inputs.map((m) => runPrompt({ message: m, listPersonId }).unwrap())
				);

				if (requestIdRef.current !== myRequestId) {
					return;
				}

				clearTimers();
				completeAllSteps();
				setResult({
					messages: responses.map((r, i) => ({
						original: inputs[i],
						final: r.message,
						columnValues: r.columnValues,
					})),
				});
			} catch (e) {
				if (requestIdRef.current !== myRequestId) {
					return;
				}

				clearTimers();
				const message_ = e instanceof Error ? e.message : "";

				setError(message_ || "error");
			} finally {
				if (requestIdRef.current === myRequestId) {
					setIsRunning(false);
				}
			}
		},
		[clearTimers, completeAllSteps, resetSteps, runPrompt]
	);

	const cancel = useCallback(() => {
		// Invalidate any in-flight run by bumping the request id and clearing timers.
		requestIdRef.current += 1;
		clearTimers();
		setIsRunning(false);
	}, [clearTimers]);

	return { stepStates, result, isRunning, error, run, cancel };
};
